// @ts-check
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Card, Title, Text, Flex } from './TremorCompat';
import { Button } from './TremorCompat';
import { MdErrorOutline as IconError, MdRefresh as IconRefresh } from 'react-icons/md';
import { COLOURS } from '../styles';

/**
 * Renders inside an ErrorBoundary when a wrapped subtree throws.
 * react-error-boundary passes `error` and `resetErrorBoundary` props.
 *
 * @param {{
 *   error: Error,
 *   resetErrorBoundary: () => void,
 *   viewName?: string,
 * }} props
 */
export const ErrorFallback = ({ error, resetErrorBoundary, viewName }) => {
  const isDev = process.env.NODE_ENV !== 'production';

  return (
    <Card decoration="left" decorationColor={COLOURS.error} className="mt-3">
      <Flex justifyContent="start" alignItems="start" className="space-x-3">
        <IconError size={32} color="red" />
        <div className="space-y-2 flex-1">
          <Title color={COLOURS.error}>Something went wrong{viewName ? ` in ${viewName}` : ''}</Title>
          <Text>
            This part of the app stopped responding. Other parts should still work. Try again — if it keeps happening,
            refresh the page or sign out and back in.
          </Text>
          {isDev && error?.message && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm text-slate-600">Developer details</summary>
              <pre className="mt-2 text-xs text-slate-700 whitespace-pre-wrap break-words">
                {error.message}
                {error.stack ? '\n\n' + error.stack : ''}
              </pre>
            </details>
          )}
          <Flex justifyContent="start" className="space-x-2 mt-3">
            <Button icon={IconRefresh} color={COLOURS.default} size="xs" onClick={resetErrorBoundary}>
              Try again
            </Button>
          </Flex>
        </div>
      </Flex>
    </Card>
  );
};

/**
 * Convenience wrapper that pairs ErrorBoundary with ErrorFallback and
 * threads a human-readable view name into the fallback UI.
 *
 * @param {{ name: string, children: React.ReactNode, onReset?: () => void }} props
 */
export const ViewErrorBoundary = ({ name, children, onReset }) => (
  <ErrorBoundary
    fallbackRender={(props) => <ErrorFallback {...props} viewName={name} />}
    onReset={onReset}
    onError={(error, info) => console.error(`[${name}] caught error:`, error, info)}
  >
    {children}
  </ErrorBoundary>
);
